import envConfig from "@/config/envConfig";
import { QUERY_PAGES_SLUG, QUERY_TAGS_SLUG } from "@/graphql/query";
import { ArticleTagType, ArticleType } from "@/types/article";
import { MediaType } from "@/types/common";
import { InterviewType } from "@/types/interviews";
import { PageType } from "@/types/page";
import { QuoteDetailType } from "@/types/quotes";
import { getAuthHeaders } from "@/utils/authToken";

export const fetchSearchData = async (lang?: string) => {
  const query = lang ? `?lang=${encodeURIComponent(lang)}` : "";
  const response = await fetch(`${envConfig.publicServerUrl}/api/v1/most-searched${query}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Error: ${response.statusText}`);
  }

  return await response.json();
};

export const fetchSearchResult = async ({
  searchQuery,
  limit,
  page,
  lang,
}: {
  searchQuery: string;
  limit: number;
  page: number;
  lang?: string;
}) => {
  try {
    const query = new URLSearchParams({
      search: searchQuery,
      limit: String(limit),
      page: String(page),
    });
    if (lang) query.set("lang", lang);

    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/search?${query.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Catch Error in fetchSearchResult:", err);
    return err;
  }
};

export const fetchNewsByReporter = async ({
  limit,
  page,
  slug,
  lang,
}: {
  limit: number;
  page: number;
  slug: string;
  lang?: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/news/author/${slug}?&limit=${limit}&page=${page}&lang=${lang}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Catch Error in fetchSearchResult:", err);
    return err;
  }
};

export const fetchInterviewsByReporter = async ({
  limit,
  page,
  slug,
  lang,
}: {
  limit: number;
  page: number;
  slug: string;
  lang: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/interviews/author/${slug}?&limit=${limit}&page=${page}&lang=${lang}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Catch Error in fetchSearchResult:", err);
    return err;
  }
};

export const fetchQuotesByReporter = async ({
  limit,
  page,
  slug,
  lang,
}: {
  limit: number;
  page: number;
  slug: string;
  lang: string;
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/quotes/author/${slug}?&limit=${limit}&page=${page}&lang=${lang}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    return await response.json();
  } catch (err) {
    console.error("Catch Error in fetchSearchResult:", err);
    return err;
  }
};

export const fetchTopBarData = async (lang: string) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/globals/topbar?locale=${lang}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-cache",
    });

    const data = await response.json();

    // console.log("fetchTopBarData data:", data);

    return data;
  } catch (err) {
    console.error("Catch Error in fetchTopBarData:", err);
    return err;
  }
};

export const fetchFooterData = async (lang: string) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/globals/footer?locale=${lang}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-cache",
    });

    const data = await response.json();

    // console.log("fetchFooterData data:", data);

    return data;
  } catch (err) {
    console.error("Catch Error in fetchFooterData:", err);
    return err;
  }
};

export const fetchSocialsData = async (lang: string) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/globals/socials?locale=${lang}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    const data = await response.json();

    // console.log("fetchSocialsData data:", data);

    return data;
  } catch (err) {
    console.error("Catch Error in fetchSocialsData:", err);
    return err;
  }
};

export const postLike = async ({
  id,
  type,
  isLiked,
}: {
  id: string;
  type: "news" | "quotes" | "interviews";
  isLiked: boolean;
}) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/like-bookmark/like`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        contentId: id,
        contentType: type,
        isLiked,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    throw err;
  }
};

export const postBookmark = async ({
  id,
  type,
  isBookmarked,
}: {
  id: string;
  type: "news" | "quotes" | "interviews";
  isBookmarked: boolean;
}) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/like-bookmark/bookmark`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        contentId: id,
        contentType: type,
        isBookmarked,
      }),
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    throw err;
  }
};

export const fetchAllBookmarks = async () => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/like-bookmark/bookmark`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData as {
      data: {
        interviews: {
          data: InterviewType[];
          count: number;
        };
        quotes: {
          data: QuoteDetailType[];
          count: number;
        };
        news: {
          data: ArticleType[];
          count: number;
        };
      };
    };
  } catch (err) {
    throw err;
  }
};

export const fetchAllLiked = async () => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/v1/like-bookmark/like`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: "include",
    });

    if (!response.ok) {
      const err = new Error(response.status === 401 ? "Unauthorized" : "Something went wrong!") as Error & { status?: number };
      err.status = response.status;
      throw err;
    }

    const respData = await response.json();
    return respData as {
      data: {
        interviews: {
          data: any[];
          count: number;
        };
        quotes: {
          data: any[];
          count: number;
        };
        news: {
          data: any[];
          count: number;
        };
      };
    };
  } catch (err) {
    throw err;
  }
};

export const fetchActivityStatus = async ({
  contentId,
  contentType,
}: {
  contentId: string;
  contentType: "quotes" | "interviews" | "news";
}) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/v1/${contentType}/activity-status/${contentId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData as { isLiked: boolean; likesCount: number; isBookmarked: boolean };
  } catch (err) {
    throw err;
  }
};

export const fetchMedia = async ({ id }: { id: string }) => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/media/${id}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Something went wrong!");
    }

    const respData = await response.json();
    return respData as MediaType;
  } catch (err) {
    throw err;
  }
};

export const fetchAllTagsSlug = async ({ limit = 10 }: { limit?: number }) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: QUERY_TAGS_SLUG,
        variables: {
          limit,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.Tags?.docs || []) as ArticleTagType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchAllPagesSlug = async ({ limit = 10 }: { limit?: number }) => {
  try {
    const response = await fetch(envConfig.publicQraphqlUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: QUERY_PAGES_SLUG,
        variables: {
          limit,
        },
      }),
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return (respData?.data?.Pages?.docs || []) as PageType[];
  } catch (err) {
    console.log(err);
  }
};

export const fetchSpecialEvents = async () => {
  try {
    const response = await fetch(`${envConfig.publicServerUrl}/api/globals/special-events`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-cache",
    });

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    console.error("Catch Error in fetchSpecialEvents:", err);
    return err;
  }
};

export const fetchGlobalCarousel = async ({ lang }: { lang: string }) => {
  try {
    const response = await fetch(
      `${envConfig.publicServerUrl}/api/globals/carousel?locale=${lang}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        cache: "no-cache",
      },
    );

    if (!response.ok) {
      throw new Error(`Error: ${response.statusText}`);
    }

    const respData = await response.json();
    return respData;
  } catch (err) {
    console.error("Catch Error in fetchSpecialEvents:", err);
    return err;
  }
};
